import React, { useEffect, useRef, useState } from 'react'
import VideoThumbnail from 'react-video-thumbnail';
import { Box, Button, CardMedia, Chip, CircularProgress, FormControl, FormControlLabel, Grid, IconButton, Modal, Paper, Radio, RadioGroup, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ReactPlayer from 'react-player';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import api from '../../../conf/api';
import { toast } from 'react-toastify';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import Cropper from 'react-easy-crop';
import { RiseLoader } from "react-spinners";
import VrpanoIcon from '@mui/icons-material/Vrpano';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import DeleteIcon from '@mui/icons-material/Delete';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import CloseIcon from '@mui/icons-material/Close';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import LayersIcon from '@mui/icons-material/Layers';
import MovieIcon from '@mui/icons-material/Movie';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import LinkIcon from '@mui/icons-material/Link';
import MovieFilterIcon from '@mui/icons-material/MovieFilter';

const THEME = '#0d9488'; // teal accent

const PRESET_STICKERS = [
  '🔥', '⭐', '💯', '🎬', '🎵', '🏆', '❤️', '😂', '👑', '✨',
  '🎯', '🚀', '💪', '🌟', '🎉', '🤩', '😎', '🎊', '🌈', '💥',
  '👍', '🙌', '💎', '🎁', '📸', '🎥', '🎤', '🌊', '⚡', '🍕',
];

// utils/cropImage.js
function getCroppedImg(imageSrc, pixelCrop, rotation = 0) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.src = imageSrc;
    image.crossOrigin = 'anonymous';

    image.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      const radians = rotation * (Math.PI / 180);

      const sin = Math.abs(Math.sin(radians));
      const cos = Math.abs(Math.cos(radians));
      const newWidth = image.width * cos + image.height * sin;
      const newHeight = image.width * sin + image.height * cos;

      canvas.width = pixelCrop.width;
      canvas.height = pixelCrop.height;

      ctx.translate(-pixelCrop.x, -pixelCrop.y);
      ctx.translate(newWidth / 2, newHeight / 2);
      ctx.rotate(radians);
      ctx.drawImage(image, -image.width / 2, -image.height / 2);

      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error("Canvas is empty"));
          return;
        }
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = () => resolve(reader.result);
      }, 'image/png');
    };

    image.onerror = (err) => reject(err);
  });
}

export async function compressBase64Image(base64, maxWidth = 480) {
  return new Promise((resolve) => {
    const img = new Image();
    img.src = base64;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const scale = maxWidth / img.width;
      canvas.width = maxWidth;
      canvas.height = img.height * scale;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/jpeg', 0.8));
    };
  });
}

async function composeOverlays(croppedBase64, overlays) {
  if (!overlays.length) return croppedBase64;
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = croppedBase64;
    img.onload = async () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);

      for (const overlay of overlays) {
        const x = (overlay.x / 100) * canvas.width;
        const y = (overlay.y / 100) * canvas.height;
        // scale relative to 250px reference preview height
        const scaledSize = (overlay.size / 250) * canvas.height;

        ctx.save();
        ctx.globalAlpha = overlay.opacity ?? 1;

        if (overlay.type === 'sticker') {
          ctx.font = `${scaledSize}px serif`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(overlay.src, x, y);
        } else if (overlay.type === 'text') {
          const weight = overlay.fontWeight || 'bold';
          const style = overlay.fontStyle || 'normal';
          ctx.font = `${style} ${weight} ${scaledSize}px sans-serif`;
          ctx.fillStyle = overlay.color || '#ffffff';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          if (overlay.textShadow !== false) {
            ctx.shadowColor = 'rgba(0,0,0,0.85)';
            ctx.shadowBlur = scaledSize * 0.15;
            ctx.shadowOffsetX = 1;
            ctx.shadowOffsetY = 1;
          }
          ctx.fillText(overlay.src || '', x, y);
        } else {
          await new Promise((res) => {
            const logoImg = new Image();
            logoImg.src = overlay.src;
            logoImg.onload = () => {
              const ar = overlay.aspectRatio || (logoImg.naturalWidth / logoImg.naturalHeight);
              const drawW = ar >= 1 ? scaledSize : scaledSize * ar;
              const drawH = ar >= 1 ? scaledSize / ar : scaledSize;
              ctx.drawImage(logoImg, x - drawW / 2, y - drawH / 2, drawW, drawH);
              res();
            };
            logoImg.onerror = res;
          });
        }

        ctx.restore();
      }

      canvas.toBlob((blob) => {
        if (!blob) { reject(new Error("Canvas is empty")); return; }
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = () => resolve(reader.result);
      }, 'image/png');
    };
    img.onerror = reject;
  });
}


function MRSSThumbnailCreator({ open, setOpen, url, onSave, refreshFunc = () => { }, capturedFrame = null }) {
  const [currentTime, setCurrentTime] = useState(0);
  const playerWrapperRef = useRef(null);
  const [thumbnail, setThumbnail] = useState(null);
  // The raw frame "Get Landscape" converts from — kept separate from
  // `thumbnail` so switching Black/Blur after a first conversion re-runs on
  // the original frame instead of on the already-landscaped result (which
  // is already full-canvas, so re-converting it is a no-op either way).
  const originalFrameRef = useRef(null);
  const [loadingImg, setImgLoading] = React.useState(false);
  const [pausedAt, setPausedAt] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Video source selection: 'captured' | 'url' | 'local'
  const [videoSource, setVideoSource] = useState('url');
  const [localVideoFile, setLocalVideoFile] = useState(null);
  const [localVideoUrl, setLocalVideoUrl] = useState(null);
  const localVideoInputRef = useRef(null);

  const activeVideoUrl = videoSource === 'local' ? localVideoUrl : url;
  const [rotation, setRotation] = useState(0);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [aspect, setAspect] = useState(9 / 16);
  const ObjectViews = ["contain", "vertical-cover", "cover", "horizontal-cover"]
  const [objectView, setObjectView] = useState("contain");
  const [landscapeConversion, setLandscapeConversion] = useState({ loading: false, fromServer: false, base64Img: null })
  const [landscapeFillMode, setLandscapeFillMode] = useState('black');

  // Overlay state
  const [overlays, setOverlays] = useState([]);
  const [draggingId, setDraggingId] = useState(null);
  const dragStartRef = useRef(null);
  const overlayBoxRef = useRef(null);
  const [showStickerPicker, setShowStickerPicker] = useState(false);
  const [croppedPreview, setCroppedPreview] = useState(null);
  const previewTimerRef = useRef(null);

  // On open: auto-trigger capture if initialTime was provided (opened from video editor)
  // On close: free all blob URLs and reset state
  useEffect(() => {
    if (open) {
      if (capturedFrame) {
        setVideoSource('captured');
        setThumbnail(capturedFrame);
        originalFrameRef.current = capturedFrame;
        setZoom(1);
      }
    } else {
      setOverlays(prev => {
        prev.forEach(o => {
          if (o.type === 'logo' && o.src?.startsWith('blob:')) URL.revokeObjectURL(o.src);
        });
        return [];
      });
      setShowStickerPicker(false);
      setCroppedPreview(null);
      // Free local video blob
      if (localVideoUrl) URL.revokeObjectURL(localVideoUrl);
      setLocalVideoUrl(null);
      setLocalVideoFile(null);
      setVideoSource('url');
      setPausedAt(null);
      setThumbnail(null);
      originalFrameRef.current = null;
      setImgLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Generate live cropped preview for the overlay editor
  useEffect(() => {
    if (!thumbnail || !croppedAreaPixels) { setCroppedPreview(null); return; }
    clearTimeout(previewTimerRef.current);
    previewTimerRef.current = setTimeout(async () => {
      try {
        const result = await getCroppedImg(thumbnail, croppedAreaPixels, rotation);
        setCroppedPreview(result);
      } catch { /* silent */ }
    }, 500);
    return () => clearTimeout(previewTimerRef.current);
  }, [thumbnail, croppedAreaPixels, rotation]);

  function formatTime(seconds) {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const handleLocalVideoSelect = (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (localVideoUrl) URL.revokeObjectURL(localVideoUrl);
    const blobUrl = URL.createObjectURL(file);
    setLocalVideoFile(file);
    setLocalVideoUrl(blobUrl);
    // Reset capture state so user pauses on the new video
    setThumbnail(null);
    originalFrameRef.current = null;
    setPausedAt(null);
    setImgLoading(false);
  };

  const handleSourceSwitch = (newSource) => {
    if (newSource === videoSource) return;
    setVideoSource(newSource);
    if (newSource === 'captured' && capturedFrame) {
      setThumbnail(capturedFrame);
      originalFrameRef.current = capturedFrame;
      setZoom(1);
      setImgLoading(false);
    } else {
      setThumbnail(null);
      setPausedAt(null);
      setImgLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!thumbnail) return;
    setUploading(true);
    try {
      const croppedBase64 = await getCroppedImg(thumbnail, croppedAreaPixels, rotation);
      const finalBase64 = await composeOverlays(croppedBase64, overlays);
      const base64 = finalBase64.split(',')[1];
      onSave(base64);
      setOpen(false);
    } catch (error) {
      console.error('Upload error:', error.response?.data || error.message);
      toast.error("Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const handleLandscapeConversion = async () => {
    const player = playerWrapperRef.current;
    if (player && player.player?.isPlaying) {
      toast.error("Stop the player to set frame.");
      return;
    }
    setLandscapeConversion(pre => ({ ...pre, loading: true }));
    try {
      // Always convert from the original frame, not from `thumbnail` — once
      // converted once, `thumbnail` is already a full 1920x1080 canvas, and
      // re-running on that is a no-op regardless of fill_mode.
      const sourceFrame = originalFrameRef.current || thumbnail;
      const response = await api.post("videos/portrait-to-landscape/", {
        thumbnail: sourceFrame,
        fill_mode: landscapeFillMode,
      }, { timeout: 10 * 60 * 1000 });
      if (!originalFrameRef.current) originalFrameRef.current = sourceFrame;
      setThumbnail(response.data.data_uri);

    } catch (error) {
      toast.error("Conversion failed:", error);
    } finally {
      setLandscapeConversion(pre => ({ ...pre, loading: false }));
    }
  };

  const handleRotation = (angle) => () => {
    setRotation((prev) => (prev + angle + 360) % 360);
  };

  // --- Overlay management ---
  const addSticker = (emoji) => {
    setOverlays(prev => [...prev, { id: Date.now(), type: 'sticker', src: emoji, x: 50, y: 50, size: 48 }]);
    setShowStickerPicker(false);
  };

  const addLogo = (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    const blobUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const aspectRatio = img.naturalWidth / img.naturalHeight;
      setOverlays(prev => [...prev, {
        id: Date.now(), type: 'logo', src: blobUrl, name: file.name,
        x: 50, y: 50, size: 80, opacity: 1, aspectRatio,
      }]);
    };
    img.src = blobUrl;
  };

  const addTextOverlay = () => {
    setOverlays(prev => [...prev, {
      id: Date.now(), type: 'text', src: 'Caption', x: 50, y: 85,
      size: 32, color: '#ffffff', fontWeight: 'bold', fontStyle: 'normal',
      textShadow: true, opacity: 1,
    }]);
  };

  const removeOverlay = (id) => setOverlays(prev => {
    const target = prev.find(o => o.id === id);
    if (target?.type === 'logo' && target.src?.startsWith('blob:')) URL.revokeObjectURL(target.src);
    return prev.filter(o => o.id !== id);
  });

  const updateOverlay = (id, changes) =>
    setOverlays(prev => prev.map(o => o.id === id ? { ...o, ...changes } : o));

  const handleOverlayMouseDown = (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    const box = overlayBoxRef.current?.getBoundingClientRect();
    if (!box) return;
    const overlay = overlays.find(o => o.id === id);
    dragStartRef.current = {
      id,
      startMouseX: e.clientX,
      startMouseY: e.clientY,
      startX: overlay.x,
      startY: overlay.y,
      boxWidth: box.width,
      boxHeight: box.height,
    };
    setDraggingId(id);
  };

  const handleBoxMouseMove = (e) => {
    const drag = dragStartRef.current;
    if (!drag) return;
    const dx = ((e.clientX - drag.startMouseX) / drag.boxWidth) * 100;
    const dy = ((e.clientY - drag.startMouseY) / drag.boxHeight) * 100;
    const newX = Math.max(0, Math.min(100, drag.startX + dx));
    const newY = Math.max(0, Math.min(100, drag.startY + dy));
    setOverlays(prev => prev.map(o => o.id === drag.id ? { ...o, x: newX, y: newY } : o));
  };

  const handleBoxMouseUp = () => {
    dragStartRef.current = null;
    setDraggingId(null);
  };

  // Touch support for mobile drag
  const handleOverlayTouchStart = (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    const touch = e.touches[0];
    const box = overlayBoxRef.current?.getBoundingClientRect();
    if (!box) return;
    const overlay = overlays.find(o => o.id === id);
    dragStartRef.current = {
      id,
      startMouseX: touch.clientX,
      startMouseY: touch.clientY,
      startX: overlay.x,
      startY: overlay.y,
      boxWidth: box.width,
      boxHeight: box.height,
    };
    setDraggingId(id);
  };

  const handleBoxTouchMove = (e) => {
    const touch = e.touches[0];
    handleBoxMouseMove(touch);
  };

  const ASPECTS = [
    { label: '4:3', value: 4 / 3 },
    { label: '16:9', value: 16 / 9 },
    { label: '9:16', value: 9 / 16 },
    { label: '3:4', value: 3 / 4 },
    { label: '1:1', value: 1 / 1 },
  ];

  const busy = loadingImg || landscapeConversion.loading;

  return (

    <Modal
      open={open}
      onClose={!uploading ? () => setOpen(false) : () => { }}
    >
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: "92vw",
        maxWidth: 1200,
        maxHeight: "95vh",
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#f8fafc',
        boxShadow: 24,
        borderRadius: 3,
        overflow: 'hidden',
      }}>
        {/* ── Gradient Header ───────────────────────────────────────────── */}
        <Box sx={{
          px: 3, py: 2,
          background: `linear-gradient(135deg, #134e4a 0%, #0f766e 50%, ${THEME} 100%)`,
          color: 'white', position: 'relative', overflow: 'hidden', flexShrink: 0,
        }}>
          <Box sx={{ position: 'absolute', top: -40, right: -30, width: 150, height: 150, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.05)', pointerEvents: 'none' }} />
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <PhotoCameraIcon sx={{ fontSize: 22 }} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={700} lineHeight={1.2}>Thumbnail Studio</Typography>
                <Typography variant="caption" sx={{ opacity: 0.75 }}>
                  Capture a frame, crop it, and add overlays
                </Typography>
              </Box>
            </Box>
            <Tooltip title="Close">
              <span>
                <IconButton onClick={() => !uploading && setOpen(false)} disabled={uploading} size="small"
                  sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.15)' } }}>
                  <CloseIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>

        {/* ── Body ──────────────────────────────────────────────────────── */}
        <Box sx={{ p: { xs: 1.5, md: 2.5 }, overflowY: 'auto', flex: 1 }}>
          <Grid container spacing={2.5}>
            {/* LEFT: Source video */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', borderColor: '#e2e8f0', height: '100%' }}>
                {/* Card header with source toggle */}
                <Box sx={{ px: 2, py: 1.25, bgcolor: alpha(THEME, 0.04), borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <MovieIcon fontSize="small" sx={{ color: THEME }} />
                  <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#0f766e' }}>Source Video</Typography>

                  {/* Source toggle */}
                  <Box display="flex" gap={0.5} sx={{ ml: 'auto' }}>
                    {[
                      ...(capturedFrame ? [{ value: 'captured', label: 'From Editor', Icon: MovieFilterIcon }] : []),
                      { value: 'url', label: 'Stream URL', Icon: LinkIcon },
                      { value: 'local', label: 'Local File', Icon: FolderOpenIcon },
                    ].map(({ value, label, Icon }) => {
                      const active = videoSource === value;
                      return (
                        <Button
                          key={value}
                          size="small"
                          variant={active ? 'contained' : 'outlined'}
                          startIcon={<Icon sx={{ fontSize: '14px !important' }} />}
                          onClick={() => handleSourceSwitch(value)}
                          sx={{
                            minWidth: 0, px: 1.25, py: 0.25, fontSize: 11, fontWeight: 700, borderRadius: 1.5, textTransform: 'none',
                            ...(active
                              ? { bgcolor: THEME, '&:hover': { bgcolor: '#0f766e' } }
                              : { color: '#0f766e', borderColor: alpha(THEME, 0.4), '&:hover': { borderColor: THEME, bgcolor: alpha(THEME, 0.06) } }),
                          }}
                        >
                          {label}
                        </Button>
                      );
                    })}
                  </Box>

                  {/* Capture status chip — only relevant for video sources */}
                  {videoSource !== 'captured' && (
                    <Chip
                      size="small"
                      label={loadingImg ? `Capturing @ ${formatTime(pausedAt ?? currentTime)}` : pausedAt !== null ? `Paused @ ${formatTime(pausedAt)}` : 'Pause to capture'}
                      sx={{ height: 22, fontSize: 11, fontWeight: 600, bgcolor: alpha(THEME, 0.1), color: '#0f766e' }}
                    />
                  )}
                </Box>

                <Box sx={{ p: 1.5 }}>
                  {/* Hidden file input for local video */}
                  <input
                    ref={localVideoInputRef}
                    type="file"
                    accept="video/*"
                    style={{ display: 'none' }}
                    onChange={handleLocalVideoSelect}
                  />

                  {/* Captured frame: show the rendered screenshot */}
                  {videoSource === 'captured' && capturedFrame && (
                    <Box sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', bgcolor: 'grey.900' }}>
                      <Box
                        component="img"
                        src={capturedFrame}
                        alt="Captured from video editor"
                        sx={{ width: '100%', display: 'block', borderRadius: 2 }}
                      />
                      <Chip
                        size="small"
                        icon={<MovieFilterIcon sx={{ fontSize: '13px !important' }} />}
                        label="Rendered by the Video Editor — captions included"
                        sx={{ position: 'absolute', bottom: 8, left: 8, height: 22, fontSize: 10, fontWeight: 600, bgcolor: 'rgba(15,23,42,0.75)', color: 'white', backdropFilter: 'blur(4px)' }}
                      />
                    </Box>
                  )}

                  {/* Local file: no file chosen yet → upload zone */}
                  {videoSource === 'local' && !localVideoFile && (
                    <Box
                      onClick={() => localVideoInputRef.current?.click()}
                      sx={{
                        minHeight: 220,
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5,
                        border: '2px dashed', borderColor: alpha(THEME, 0.35),
                        borderRadius: 2, bgcolor: alpha(THEME, 0.03),
                        cursor: 'pointer', transition: 'all 0.2s',
                        '&:hover': { borderColor: THEME, bgcolor: alpha(THEME, 0.07) },
                      }}
                    >
                      <Box sx={{ width: 52, height: 52, borderRadius: '50%', bgcolor: alpha(THEME, 0.12), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <FolderOpenIcon sx={{ color: THEME, fontSize: 26 }} />
                      </Box>
                      <Typography variant="body2" fontWeight={700} color="#0f766e">Choose a video file</Typography>
                      <Typography variant="caption" color="text.secondary" textAlign="center">
                        MP4, MOV, WebM and other formats supported
                      </Typography>
                      <Button
                        variant="contained" size="small"
                        startIcon={<CloudUploadIcon />}
                        sx={{ mt: 0.5, borderRadius: 1.5, textTransform: 'none', fontWeight: 700, bgcolor: THEME, '&:hover': { bgcolor: '#0f766e' } }}
                      >
                        Browse Files
                      </Button>
                    </Box>
                  )}

                  {/* Player — shown when source=url, or source=local with a file */}
                  {(videoSource === 'url' || (videoSource === 'local' && localVideoFile)) && (
                    <>
                      {videoSource === 'local' && localVideoFile && (
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <Chip
                            size="small"
                            icon={<FolderOpenIcon sx={{ fontSize: '14px !important' }} />}
                            label={localVideoFile.name}
                            onDelete={() => localVideoInputRef.current?.click()}
                            deleteIcon={<Tooltip title="Change file"><FolderOpenIcon sx={{ fontSize: '14px !important' }} /></Tooltip>}
                            sx={{ maxWidth: '100%', height: 24, fontSize: 11, fontWeight: 600, bgcolor: alpha(THEME, 0.1), color: '#0f766e', '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
                          />
                        </Box>
                      )}
                      <CardMedia
                        component={ReactPlayer}
                        url={activeVideoUrl}
                        controls
                        width="100%"
                        height="auto"
                        ref={playerWrapperRef}
                        onProgress={({ playedSeconds }) => setCurrentTime(playedSeconds)}
                        onPause={() => { setImgLoading(true); setPausedAt(currentTime); }}
                        sx={{
                          maxHeight: "320px",
                          minHeight: 200,
                          borderRadius: 2,
                          bgcolor: "grey.900",
                          overflow: 'hidden',
                          "& video": { maxHeight: "320px !important", borderRadius: 8 }
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                        Scrub to the moment you want, then pause — the loader appears immediately while the frame is captured.
                      </Typography>
                    </>
                  )}
                </Box>
              </Paper>
            </Grid>

            {/* RIGHT: Crop & adjust */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', borderColor: '#e2e8f0', height: '100%' }}>
                <Box sx={{ px: 2, py: 1.25, bgcolor: alpha(THEME, 0.04), borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AspectRatioIcon fontSize="small" sx={{ color: THEME }} />
                  <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#0f766e' }}>Crop & Adjust</Typography>
                </Box>

                <Box sx={{ p: 1.5 }}>
                  {/* Hidden frame grabber */}
                  {videoSource !== 'captured' && !landscapeConversion.fromServer && pausedAt !== null && activeVideoUrl &&
                    <Box hidden>
                      <VideoThumbnail
                        key={pausedAt}
                        videoUrl={activeVideoUrl}
                        thumbnailHandler={async (_thumbnail) => {
                          try {
                            setZoom(0);
                            const img = new Image();
                            img.src = _thumbnail;
                            img.onload = () => {
                              setThumbnail(_thumbnail);
                              originalFrameRef.current = _thumbnail;
                              setZoom(1);
                            };
                            img.onerror = () => {
                              toast.error("Failed to load image.");
                              setImgLoading(false);
                            };
                          } catch (err) {
                            toast.error("Failed to process thumbnail");
                            setImgLoading(false);
                          }
                        }}
                        snapshotAtTime={pausedAt.toFixed(2)}
                      />
                    </Box>
                  }

                  {/* Cropper area with proper loader overlay */}
                  <Box position="relative" width="100%" height={300} borderRadius={2} overflow="hidden" sx={{ bgcolor: 'grey.900' }}>
                    {/* Loader overlay — sits on top, always visible while busy */}
                    {busy && (
                      <Box sx={{
                        position: 'absolute', inset: 0, zIndex: 5,
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2,
                        bgcolor: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(2px)',
                      }}>
                        <RiseLoader color={THEME} size={16} />
                        <Typography variant="caption" sx={{ color: 'white', fontWeight: 600 }}>
                          {landscapeConversion.loading ? 'Converting to landscape…' : 'Capturing frame…'}
                        </Typography>
                      </Box>
                    )}

                    {/* Empty state when no frame yet */}
                    {!thumbnail && !busy && (
                      <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1, color: 'grey.500' }}>
                        <ImageNotSupportedIcon sx={{ fontSize: 40, opacity: 0.5 }} />
                        <Typography variant="caption">Pause the video to capture a frame</Typography>
                      </Box>
                    )}

                    {/* Cropper — only mount when we have an image */}
                    {thumbnail && (
                      <Box sx={{ position: 'absolute', inset: 0, opacity: loadingImg ? 0.25 : 1, transition: 'opacity 0.4s ease-in' }}>
                        <Cropper
                          image={thumbnail}
                          crop={crop}
                          zoom={zoom}
                          maxZoom={1.482}
                          minZoom={1}
                          rotation={rotation}
                          aspect={aspect}
                          onCropChange={setCrop}
                          onZoomChange={setZoom}
                          objectFit={objectView}
                          onMediaLoaded={() => { setImgLoading(false) }}
                          onCropComplete={(_, croppedPixels) => setCroppedAreaPixels(croppedPixels)}
                        />
                      </Box>
                    )}
                  </Box>

                  {/* Aspect ratio + rotation */}
                  <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mt={1.5} flexWrap="wrap">
                    <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
                      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mr: 0.5 }}>Ratio</Typography>
                      {ASPECTS.map(a => {
                        const active = aspect === a.value;
                        return (
                          <Button
                            key={a.label}
                            variant={active ? 'contained' : 'outlined'}
                            size="small"
                            onClick={() => setAspect(a.value)}
                            sx={{
                              minWidth: 0, px: 1.25, py: 0.25, fontSize: 12, fontWeight: 700, borderRadius: 1.5,
                              ...(active
                                ? { bgcolor: THEME, '&:hover': { bgcolor: '#0f766e' } }
                                : { color: '#0f766e', borderColor: alpha(THEME, 0.4), '&:hover': { borderColor: THEME, bgcolor: alpha(THEME, 0.06) } }),
                            }}
                          >
                            {a.label}
                          </Button>
                        );
                      })}
                    </Box>
                    <Box display="flex" gap={0.5}>
                      <Tooltip title="Rotate left">
                        <IconButton size="small" onClick={handleRotation(-90)} sx={{ color: THEME, '&:hover': { bgcolor: alpha(THEME, 0.1) } }}>
                          <RotateLeftIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Rotate right">
                        <IconButton size="small" onClick={handleRotation(90)} sx={{ color: THEME, '&:hover': { bgcolor: alpha(THEME, 0.1) } }}>
                          <RotateRightIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  {/* Object view */}
                  <Box display="flex" alignItems="center" gap={1} mt={1}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ flexShrink: 0 }}>Fit</Typography>
                    <FormControl size='small'>
                      <RadioGroup
                        row
                        aria-labelledby="CropView"
                        value={objectView}
                        onChange={(e, new_value) => { setObjectView(new_value) }}
                      >
                        {ObjectViews.map(obv => (
                          <FormControlLabel
                            key={obv}
                            value={obv}
                            control={<Radio size='small' sx={{ py: 0.25, '&.Mui-checked': { color: THEME } }} />}
                            label={<Typography variant="caption">{obv.toUpperCase()}</Typography>}
                          />
                        ))}
                      </RadioGroup>
                    </FormControl>
                  </Box>
                </Box>
              </Paper>
            </Grid>

            {/* OVERLAYS — full width */}
            <Grid item xs={12}>
              {thumbnail && (
                <Paper elevation={0} variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', borderColor: '#e2e8f0' }}>
                  <Box sx={{ px: 2, py: 1.25, bgcolor: alpha(THEME, 0.04), borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <LayersIcon fontSize="small" sx={{ color: THEME }} />
                      <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#0f766e' }}>Overlays</Typography>
                      {overlays.length > 0 && (
                        <Chip size="small" label={overlays.length} sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: alpha(THEME, 0.12), color: '#0f766e' }} />
                      )}
                    </Box>
                    <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                      {/* Text caption */}
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<TextFieldsIcon />}
                        onClick={addTextOverlay}
                        sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600, color: '#0f766e', borderColor: alpha(THEME, 0.4), '&:hover': { borderColor: THEME, bgcolor: alpha(THEME, 0.06) } }}
                      >
                        Add Text
                      </Button>

                      {/* Logo upload */}
                      <Tooltip title="Upload a logo or image to overlay">
                        <Button
                          variant="outlined"
                          size="small"
                          component="label"
                          startIcon={<AddPhotoAlternateIcon />}
                          sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600, color: '#0f766e', borderColor: alpha(THEME, 0.4), '&:hover': { borderColor: THEME, bgcolor: alpha(THEME, 0.06) } }}
                        >
                          Add Logo
                          <input type="file" hidden accept="image/*" onChange={addLogo} />
                        </Button>
                      </Tooltip>

                      {/* Sticker picker */}
                      <Box position="relative">
                        <Button
                          variant={showStickerPicker ? 'contained' : 'outlined'}
                          size="small"
                          startIcon={<EmojiEmotionsIcon />}
                          onClick={() => setShowStickerPicker(p => !p)}
                          sx={{
                            borderRadius: 1.5, textTransform: 'none', fontWeight: 600,
                            ...(showStickerPicker
                              ? { bgcolor: THEME, '&:hover': { bgcolor: '#0f766e' } }
                              : { color: '#0f766e', borderColor: alpha(THEME, 0.4), '&:hover': { borderColor: THEME, bgcolor: alpha(THEME, 0.06) } }),
                          }}
                        >
                          Stickers
                        </Button>
                        {showStickerPicker && (
                          <Box sx={{
                            position: 'absolute',
                            top: '100%',
                            right: 0,
                            zIndex: 200,
                            bgcolor: 'background.paper',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                            p: 1,
                            display: 'grid',
                            gridTemplateColumns: 'repeat(6, 1fr)',
                            gap: 0.25,
                            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                            mt: 0.5,
                            minWidth: 220,
                          }}>
                            {PRESET_STICKERS.map(emoji => (
                              <IconButton
                                key={emoji}
                                size="small"
                                onClick={() => addSticker(emoji)}
                                sx={{ fontSize: '1.4rem', p: 0.5, '&:hover': { bgcolor: alpha(THEME, 0.1) } }}
                              >
                                {emoji}
                              </IconButton>
                            ))}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={{ p: 1.5 }}>
                  {/* Overlay preview — drag stickers/logos here */}
                  <Box
                    ref={overlayBoxRef}
                    position="relative"
                    width="100%"
                    height={280}
                    sx={{
                      border: '1px dashed',
                      borderColor: alpha(THEME, 0.3),
                      borderRadius: 2,
                      overflow: 'hidden',
                      bgcolor: 'grey.900',
                      cursor: draggingId ? 'grabbing' : 'default',
                      userSelect: 'none',
                    }}
                    onMouseMove={handleBoxMouseMove}
                    onMouseUp={handleBoxMouseUp}
                    onMouseLeave={handleBoxMouseUp}
                    onTouchMove={handleBoxTouchMove}
                    onTouchEnd={handleBoxMouseUp}
                  >
                    {croppedPreview ? (
                      <Box
                        component="img"
                        src={croppedPreview}
                        alt="cropped preview"
                        sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                      />
                    ) : (
                      <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                        <Typography variant="caption" color="grey.500">
                          {thumbnail ? 'Generating preview…' : 'Capture a frame first'}
                        </Typography>
                      </Box>
                    )}

                    {/* Draggable overlay items */}
                    {overlays.map(overlay => (
                      <Box
                        key={overlay.id}
                        sx={{
                          position: 'absolute',
                          left: `${overlay.x}%`,
                          top: `${overlay.y}%`,
                          transform: 'translate(-50%, -50%)',
                          cursor: draggingId === overlay.id ? 'grabbing' : 'grab',
                          lineHeight: 1,
                          zIndex: 10,
                          filter: draggingId === overlay.id
                            ? 'drop-shadow(0 0 5px rgba(255,255,255,0.9))'
                            : 'drop-shadow(0 1px 3px rgba(0,0,0,0.6))',
                        }}
                        onMouseDown={(e) => handleOverlayMouseDown(e, overlay.id)}
                        onTouchStart={(e) => handleOverlayTouchStart(e, overlay.id)}
                      >
                        {overlay.type === 'sticker' ? (
                          <span style={{ fontSize: overlay.size, display: 'block', lineHeight: 1, opacity: overlay.opacity ?? 1 }}>
                            {overlay.src}
                          </span>
                        ) : overlay.type === 'text' ? (
                          <span style={{
                            fontSize: overlay.size,
                            color: overlay.color || '#ffffff',
                            fontWeight: overlay.fontWeight || 'bold',
                            fontStyle: overlay.fontStyle || 'normal',
                            opacity: overlay.opacity ?? 1,
                            textShadow: (overlay.textShadow ?? true) ? '1px 1px 4px rgba(0,0,0,0.9)' : 'none',
                            whiteSpace: 'nowrap',
                            lineHeight: 1.2,
                            display: 'block',
                          }}>
                            {overlay.src || 'Caption'}
                          </span>
                        ) : (
                          <img
                            src={overlay.src}
                            alt="logo overlay"
                            style={{
                              width: overlay.aspectRatio
                                ? (overlay.aspectRatio >= 1 ? overlay.size : overlay.size * overlay.aspectRatio)
                                : overlay.size,
                              height: overlay.aspectRatio
                                ? (overlay.aspectRatio >= 1 ? overlay.size / overlay.aspectRatio : overlay.size)
                                : overlay.size,
                              display: 'block',
                              opacity: overlay.opacity ?? 1,
                            }}
                            draggable={false}
                          />
                        )}
                      </Box>
                    ))}
                  </Box>

                  {overlays.length === 0 ? (
                    <Typography variant="caption" color="text.secondary" display="block" mt={1} textAlign="center">
                      Add text captions, logos, or stickers above — then drag them to reposition on the preview.
                    </Typography>
                  ) : (
                    <Box mt={1.5} display="flex" flexDirection="column" gap={0.75}>
                      {overlays.map((overlay, idx) => (
                        <Box
                          key={overlay.id}
                          sx={{
                            p: 1,
                            border: '1px solid',
                            borderColor: '#e2e8f0',
                            borderLeft: '3px solid',
                            borderLeftColor: THEME,
                            borderRadius: 1.5,
                            bgcolor: 'white',
                          }}
                        >
                          {/* Row 1: icon + name/text-input + delete */}
                          <Box display="flex" alignItems="center" gap={1}>
                            {overlay.type === 'sticker' ? (
                              <span style={{ fontSize: 20, lineHeight: 1, flexShrink: 0 }}>{overlay.src}</span>
                            ) : overlay.type === 'text' ? (
                              <TextFieldsIcon fontSize="small" color="action" sx={{ flexShrink: 0 }} />
                            ) : (
                              <Box component="img" src={overlay.src} alt="" sx={{ maxWidth: 36, maxHeight: 22, objectFit: 'contain', borderRadius: 0.5, flexShrink: 0 }} />
                            )}

                            {overlay.type === 'text' ? (
                              <input
                                value={overlay.src}
                                onChange={(e) => updateOverlay(overlay.id, { src: e.target.value })}
                                placeholder="Enter caption…"
                                style={{
                                  flex: 1, minWidth: 0, border: '1px solid #bbb', borderRadius: 4,
                                  padding: '2px 6px', fontSize: 13, background: 'transparent',
                                  color: 'inherit', outline: 'none',
                                }}
                              />
                            ) : (
                              <Typography variant="caption" flex={1} noWrap color="text.secondary">
                                {overlay.type === 'sticker' ? `Sticker ${idx + 1}` : (overlay.name || `Logo ${idx + 1}`)}
                              </Typography>
                            )}

                            <Tooltip title="Remove">
                              <IconButton size="small" color="error" onClick={() => removeOverlay(overlay.id)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>

                          {/* Row 2: size + opacity + text controls */}
                          <Box display="flex" flexWrap="wrap" alignItems="center" gap={1} mt={0.5}>
                            {/* Size */}
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Typography variant="caption" color="text.secondary">Size</Typography>
                              <input
                                type="range"
                                min={overlay.type === 'text' ? 10 : 16}
                                max={overlay.type === 'text' ? 120 : 160}
                                value={overlay.size}
                                onChange={(e) => updateOverlay(overlay.id, { size: Number(e.target.value) })}
                                style={{ width: 70, accentColor: THEME }}
                              />
                            </Box>

                            {/* Opacity */}
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Typography variant="caption" color="text.secondary">Opacity</Typography>
                              <input
                                type="range"
                                min={10}
                                max={100}
                                value={Math.round((overlay.opacity ?? 1) * 100)}
                                onChange={(e) => updateOverlay(overlay.id, { opacity: Number(e.target.value) / 100 })}
                                style={{ width: 70, accentColor: THEME }}
                              />
                              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 28 }}>
                                {Math.round((overlay.opacity ?? 1) * 100)}%
                              </Typography>
                            </Box>

                            {/* Text-only controls */}
                            {overlay.type === 'text' && (
                              <>
                                {/* Color swatches */}
                                <Box display="flex" alignItems="center" gap={0.5}>
                                  <Typography variant="caption" color="text.secondary">Color</Typography>
                                  {['#ffffff', '#000000', '#ffff00', '#ff4444', '#00d4ff', '#ff8800', '#00e676'].map(c => (
                                    <Box
                                      key={c}
                                      onClick={() => updateOverlay(overlay.id, { color: c })}
                                      sx={{
                                        width: 15, height: 15, borderRadius: '50%', bgcolor: c, flexShrink: 0,
                                        border: overlay.color === c ? '2px solid' : '1px solid',
                                        borderColor: overlay.color === c ? 'primary.main' : 'divider',
                                        cursor: 'pointer',
                                        boxShadow: c === '#ffffff' ? 'inset 0 0 0 1px #ccc' : 'none',
                                      }}
                                    />
                                  ))}
                                  <Tooltip title="Custom color">
                                    <Box component="label" sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                                      <input
                                        type="color"
                                        value={overlay.color || '#ffffff'}
                                        onChange={(e) => updateOverlay(overlay.id, { color: e.target.value })}
                                        style={{ width: 18, height: 18, border: 'none', cursor: 'pointer', padding: 0, borderRadius: 2 }}
                                      />
                                    </Box>
                                  </Tooltip>
                                </Box>

                                {/* Bold / Italic */}
                                <Box display="flex" gap={0.5}>
                                  <Tooltip title="Bold">
                                    <IconButton
                                      size="small"
                                      onClick={() => updateOverlay(overlay.id, { fontWeight: overlay.fontWeight === 'bold' ? 'normal' : 'bold' })}
                                      sx={{
                                        border: '1px solid',
                                        borderColor: overlay.fontWeight === 'bold' ? 'primary.main' : 'divider',
                                        color: overlay.fontWeight === 'bold' ? 'primary.main' : 'action.active',
                                        p: 0.25,
                                      }}
                                    >
                                      <FormatBoldIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Italic">
                                    <IconButton
                                      size="small"
                                      onClick={() => updateOverlay(overlay.id, { fontStyle: overlay.fontStyle === 'italic' ? 'normal' : 'italic' })}
                                      sx={{
                                        border: '1px solid',
                                        borderColor: overlay.fontStyle === 'italic' ? 'primary.main' : 'divider',
                                        color: overlay.fontStyle === 'italic' ? 'primary.main' : 'action.active',
                                        p: 0.25,
                                      }}
                                    >
                                      <FormatItalicIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>

                                {/* Shadow toggle */}
                                <Button
                                  size="small"
                                  variant={(overlay.textShadow ?? true) ? 'contained' : 'outlined'}
                                  onClick={() => updateOverlay(overlay.id, { textShadow: !(overlay.textShadow ?? true) })}
                                  sx={{ minWidth: 0, px: 1, py: 0.25, fontSize: '0.65rem' }}
                                >
                                  Shadow
                                </Button>
                              </>
                            )}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                  </Box>{/* end overlays padding wrapper */}
                </Paper>
              )}
            </Grid>
          </Grid>
        </Box>

        {/* ── Footer actions ────────────────────────────────────────────── */}
        <Box sx={{
          px: 3, py: 2, bgcolor: 'white', borderTop: '1px solid #e2e8f0',
          display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0, flexWrap: 'wrap',
        }}>
          <Button
            onClick={() => !uploading && setOpen(false)}
            disabled={uploading}
            variant="outlined"
            sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600, borderColor: '#e2e8f0', color: 'text.secondary' }}
          >
            Cancel
          </Button>
          <Box sx={{ flex: 1 }} />
          <ToggleButtonGroup
            size="small"
            exclusive
            value={landscapeFillMode}
            onChange={(e, value) => value && setLandscapeFillMode(value)}
            disabled={landscapeConversion.loading || uploading}
          >
            <ToggleButton value="black">Black sides</ToggleButton>
            <ToggleButton value="blur">Blurred sides</ToggleButton>
          </ToggleButtonGroup>
          <Button
            disabled={landscapeConversion.loading || !thumbnail || uploading}
            variant="outlined"
            startIcon={landscapeConversion.loading ? <CircularProgress size={16} color="inherit" /> : <VrpanoIcon />}
            onClick={handleLandscapeConversion}
            sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600, color: '#0f766e', borderColor: alpha(THEME, 0.4), '&:hover': { borderColor: THEME, bgcolor: alpha(THEME, 0.06) } }}
          >
            {landscapeConversion.loading ? 'Converting…' : 'Get Landscape'}
          </Button>
          <Button
            variant="contained"
            endIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
            onClick={handleUpload}
            disabled={uploading || !thumbnail || landscapeConversion.loading || !activeVideoUrl}
            sx={{
              borderRadius: 1.5, textTransform: 'none', fontWeight: 700, px: 3,
              background: `linear-gradient(135deg, #0f766e, ${THEME})`,
              '&:hover': { background: 'linear-gradient(135deg, #115e59, #0f766e)' },
            }}
          >
            {uploading ? 'Uploading…' : 'Upload Thumbnail'}
          </Button>
        </Box>
      </Box>
    </Modal >

  )
}

export default MRSSThumbnailCreator
